/**
 * EditToggle — the per-view view↔edit stance switch (a workbench primitive).
 *
 * A square icon-button that flips the active view's {@link useEditMode} stance:
 * pencil = editing (armed), eye = viewing. Mounts in EVERY view's chrome
 * (process header, board toolbar, …) so the affordance is universal. It renders
 * nothing unless `canEdit` — an edit toggle you can't act on is noise — so the
 * caller passes whether mutations are wired (typically `stepEdit?.canEdit` or
 * the board's `useModelMutationsOptional() != null`).
 */
import { useContext } from "react";
import { Eye, Pencil } from "lucide-react";
import { HudChip } from "../../components/HudChip.js";
import { EditModeContext } from "../EditModeContext.js";

export interface EditToggleProps {
  /** Whether edits are possible at all (mutations wired). Hidden when false. */
  readonly canEdit: boolean;
  readonly className?: string;
}

export function EditToggle({ canEdit, className }: EditToggleProps) {
  // Optional read: a universal primitive mounts in many chromes, some outside a
  // provider (isolated tests) — degrade to nothing rather than throw.
  const api = useContext(EditModeContext);
  if (!canEdit || !api) return null;
  const { editMode, toggle } = api;
  return (
    <HudChip
      active={editMode}
      accent="arch"
      aria-label={editMode ? "Editing — click to view" : "Viewing — click to edit"}
      aria-pressed={editMode}
      title={editMode ? "Editing (click to view)" : "Edit"}
      data-edit-toggle=""
      data-edit-mode={editMode || undefined}
      className={className}
      onClick={toggle}
    >
      {editMode ? <Pencil size={14} /> : <Eye size={14} />}
    </HudChip>
  );
}
